import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["messages", "input", "form", "suggestions"]
    static values = {
        faaAdId: Number,
        conversationId: Number,
        aiEnabled: Boolean
    }

    connect() {
        this.isLoading = false

        if (this.aiEnabledValue) {
            this.loadOrCreateConversation()
        }
    }

    // Load existing conversation for this AD or create a new one
    async loadOrCreateConversation() {
        try {
            const response = await fetch(`/ai_conversations/current.json?faa_ad_id=${this.faaAdIdValue}`, {
                headers: this.jsonHeaders()
            })

            if (response.ok) {
                const data = await response.json()
                if (data.conversation) {
                    this.conversationIdValue = data.conversation.id
                    this.loadMessages(data.messages || [])
                    return
                }
            }
        } catch (error) {
            console.log('No existing AD conversation, will create on first message:', error.message)
        }
    }

    async createConversation() {
        const response = await fetch('/ai_conversations', {
            method: 'POST',
            headers: this.jsonHeaders(),
            body: JSON.stringify({ faa_ad_id: this.faaAdIdValue })
        })

        if (response.ok) {
            const data = await response.json()
            this.conversationIdValue = data.id
        } else {
            throw new Error('Failed to create AD conversation')
        }
    }

    // Handle suggested question button clicks
    async askSuggested(event) {
        event.preventDefault()
        event.stopPropagation()

        const question = event.currentTarget.dataset.question
        if (!question || this.isLoading) return

        await this.processQuestion(question)
    }

    async sendMessage(event) {
        event.preventDefault()

        if (this.isLoading) return

        const message = this.inputTarget.value.trim()
        if (!message) return

        this.inputTarget.value = ''
        await this.processQuestion(message)
    }

    async processQuestion(message) {
        this.setLoading(true)

        try {
            // Add user message to UI immediately
            this.addMessage(message, 'user')

            // Hide suggestions after first message
            if (this.hasSuggestionsTarget) {
                this.suggestionsTarget.style.display = 'none'
            }

            // Ensure we have a conversation
            if (!this.conversationIdValue) {
                await this.createConversation()
            }

            // Use streaming
            await this.submitMessageStreaming(message)

        } catch (error) {
            console.error('Error sending message:', error)
            this.addMessage('Sorry, something went wrong. Please try again.', 'assistant')
        } finally {
            this.removeStreamingCursor()
            this.setLoading(false)
        }
    }

    // SSE streaming message submission
    async submitMessageStreaming(content) {
        if (!this.conversationIdValue) {
            throw new Error('No conversation available')
        }

        const url = `/ai_conversations/${encodeURIComponent(this.conversationIdValue)}/stream_message`
        const messageContainer = this.createStreamingMessageContainer()

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    ...this.jsonHeaders(),
                    'Accept': 'text/event-stream'
                },
                body: JSON.stringify({ message: content })
            })

            if (!response.ok) {
                const errorText = await response.text().catch(() => '')
                try {
                    const errorData = JSON.parse(errorText)
                    throw new Error(errorData.error || `HTTP ${response.status}`)
                } catch (e) {
                    if (e.message.includes('subscription')) throw e
                    if (e.message.includes('limit')) throw e
                    throw new Error('Server error - please try again')
                }
            }

            await this.processStreamResponse(response, messageContainer)

        } catch (error) {
            if (messageContainer && messageContainer.parentNode) {
                messageContainer.remove()
            }
            throw error
        }
    }

    // Process SSE stream response
    async processStreamResponse(response, messageContainer) {
        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let fullContent = ''

        try {
            while (true) {
                const { done, value } = await reader.read()
                if (done) break

                buffer += decoder.decode(value, { stream: true })

                const lines = buffer.split('\n')
                buffer = lines.pop() || ''

                let eventType = null
                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        eventType = line.slice(7).trim()
                    } else if (line.startsWith('data: ') && eventType) {
                        const data = line.slice(6)
                        try {
                            const parsed = JSON.parse(data)
                            fullContent = this.handleStreamEvent(eventType, parsed, messageContainer, fullContent)
                        } catch (e) {
                            // Skip unparseable chunks
                        }
                        eventType = null
                    }
                }
            }
        } finally {
            reader.releaseLock()
        }

        this.removeStreamingCursor()
    }

    // Handle individual SSE events
    handleStreamEvent(eventType, data, messageContainer, currentContent) {
        const contentElement = messageContainer.querySelector('.streaming-content')

        switch (eventType) {
            case 'token':
                currentContent += data
                if (contentElement) {
                    contentElement.innerHTML = this.formatContent(currentContent)
                }
                this.scrollToBottom()
                break

            case 'complete':
                currentContent = data
                if (contentElement) {
                    contentElement.innerHTML = this.formatContent(data)
                }
                messageContainer.classList.remove('streaming')
                this.scrollToBottom()
                break

            case 'links':
                if (contentElement && data) {
                    currentContent += data
                    contentElement.innerHTML = this.formatContent(currentContent)
                }
                this.scrollToBottom()
                break

            case 'error':
                if (contentElement) {
                    contentElement.innerHTML = `<span class="streaming-error">${this.formatContent(data)}</span>`
                }
                messageContainer.classList.remove('streaming')
                messageContainer.classList.add('error-message')
                break
        }

        return currentContent
    }

    createStreamingMessageContainer() {
        const messageDiv = document.createElement('div')
        messageDiv.className = 'message assistant-message streaming'
        messageDiv.innerHTML = `
            <span class="streaming-content"></span>
            <span class="streaming-cursor"></span>
        `

        this.messagesTarget.appendChild(messageDiv)
        this.scrollToBottom()

        return messageDiv
    }

    removeStreamingCursor() {
        const cursors = this.messagesTarget.querySelectorAll('.streaming-cursor')
        cursors.forEach(cursor => cursor.remove())

        const streamingMessages = this.messagesTarget.querySelectorAll('.streaming')
        streamingMessages.forEach(msg => msg.classList.remove('streaming'))
    }

    loadMessages(messages) {
        messages.forEach(msg => {
            this.addMessage(msg.content, msg.role)
        })
    }

    addMessage(content, role) {
        const messageDiv = document.createElement('div')
        messageDiv.className = `message ${role}-message`
        messageDiv.innerHTML = this.formatContent(content)

        this.messagesTarget.appendChild(messageDiv)
        this.scrollToBottom()
    }

    formatContent(content) {
        // Convert markdown headings (must happen before \n -> <br>)
        let formatted = content.replace(/^### +(.+)$/gm, '<strong style="display:block; margin:8px 0 4px; font-size:1em;">$1</strong>')
        formatted = formatted.replace(/^## +(.+)$/gm, '<strong style="display:block; margin:10px 0 4px; font-size:1.05em;">$1</strong>')
        formatted = formatted.replace(/^# +(.+)$/gm, '<strong style="display:block; margin:12px 0 4px; font-size:1.1em;">$1</strong>')

        // Convert markdown bold (**text**) to HTML
        formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

        // Convert markdown links ([text](url)) to HTML
        formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #667eea; text-decoration: underline;" target="_blank">$1</a>')

        // Convert line breaks to <br>
        formatted = formatted.replace(/\n/g, '<br>')

        // Convert horizontal rules (---) to HTML
        formatted = formatted.replace(/---/g, '<hr style="border: none; border-top: 1px solid #e9ecef; margin: 10px 0;">')

        return formatted
    }

    scrollToBottom() {
        const messages = this.messagesTarget
        messages.scrollTop = messages.scrollHeight
    }

    setLoading(loading) {
        this.isLoading = loading
        const submitBtn = this.formTarget.querySelector('button[type="submit"]')
        const input = this.inputTarget

        if (submitBtn) submitBtn.disabled = loading
        if (input) input.disabled = loading
    }

    jsonHeaders() {
        const token = document.querySelector('meta[name="csrf-token"]')
        return {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token ? { 'X-CSRF-Token': token.content } : {})
        }
    }
}
